import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Link as LinkIcon, CheckCircle2, RefreshCw, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface OnChainReceiptsProps {
  companyId: string;
}

interface Receipt {
  id: string;
  event_type: string;
  ref_id: string;
  sha256: string;
  chain: string;
  tx_hash: string | null;
  verifier_url: string | null;
  timestamp: string;
}

export default function OnChainReceipts({ companyId }: OnChainReceiptsProps) {
  const [rows, setRows] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState<string>('');

  async function load() {
    setLoading(true);
    try {
      const { data, error } = (await supabase
        .from('onchain_receipts' as any)
        .select('*')
        .eq('company_id', companyId)
        .order('timestamp', { ascending: false })) as any;

      if (error) {
        console.error('Error loading receipts:', error);
        toast.error('Failed to load on-chain receipts');
      } else {
        setRows(data || []);
      }
    } catch (error: any) {
      console.error('Error loading receipts:', error);
      toast.error('Failed to load on-chain receipts');
    } finally {
      setLoading(false);
    }
  }

  async function verify(id: string) {
    setVerifying(id);
    try {
      const { data, error } = await supabase.functions.invoke('oc-verify', {
        body: { receiptId: id },
      });

      if (error) {
        console.error('Verification error:', error);
        toast.error('Verification failed');
      } else if (data?.ok) {
        toast.success('On-chain proof verified ✓');
      } else {
        toast.error('Verification failed');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      toast.error('Verification failed');
    } finally {
      setVerifying('');
      load();
    }
  }

  useEffect(() => {
    if (companyId) {
      load();
    }
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle>On-Chain Receipts</CardTitle>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length ? (
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Ref ID</TableHead>
                  <TableHead>Chain</TableHead>
                  <TableHead>Transaction</TableHead>
                  <TableHead>Hash (SHA-256)</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Badge variant="outline">{r.event_type}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.ref_id.slice(0, 8)}...</TableCell>
                    <TableCell>
                      <Badge>{r.chain}</Badge>
                    </TableCell>
                    <TableCell>
                      {r.tx_hash ? (
                        <a
                          href={r.verifier_url || `https://basescan.org/tx/${r.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          <LinkIcon className="h-3 w-3" />
                          {r.tx_hash.slice(0, 10)}...
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs truncate max-w-[200px]">
                      {r.sha256}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => verify(r.id)}
                        disabled={verifying === r.id}
                      >
                        {verifying === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Verify
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <Shield className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground">No on-chain receipts yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Key events will be automatically notarized on-chain
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
